import { useEffect } from 'react';

import { WORKSPACE_TABS, tabCommandId } from '@ai-studio/shared';

import { retryPrism } from '../engine/prism.js';
import { openAsset } from '../lib/open-asset.js';
import { registerPie, openPie } from '../lib/pies.js';
import { entityKind, entityName } from '../lib/scene-doc.js';
import { registerSearchProvider, scoreOf, type SearchItem } from '../lib/search.js';
import {
  CORE_COMMANDS,
  formatChord,
  installShortcutDispatcher,
  keyHint,
  parseChord,
  recentCommands,
  setViewContextProvider,
  shortcuts,
} from '../lib/shortcuts/index.js';
import { LEGACY_COMMANDS } from '../lib/shortcuts/legacy.js';
import { storeHistorySource, undoService } from '../lib/undo.js';
import { WORKSPACE_PRESETS, useLayout } from '../state/layout.js';
import { useModel } from '../state/model.js';
import { usePixel } from '../state/pixel.js';
import { usePlay } from '../state/play.js';
import { assetKind, useProjectFiles, type AssetKind } from '../state/project-files.js';
import { useScene } from '../state/scene.js';
import { useSound } from '../state/sound.js';
import { useGraph } from '../state/graph.js';
import { useWorkspace, type WorkspaceView } from '../state/workspace.js';
import { openSearch, toggleSearch } from './CommandPalette.js';
import { openFavoritesAtPointer } from './PieMenu.js';
import { PROFILER_COMMANDS } from './profiler/profiler-commands.js';
import { PROCGEN_COMMANDS } from './procgen/procgen-commands.js';
import { PARTICLE_EDITOR_PANEL, toggleParticleEditor } from './particles/open.js';
import { SETTINGS_SECTIONS } from './settings/ProjectSettings.js';
import { openSettings, openShortcutsFor, saveLayoutAs } from './SettingsView.js';
import { useHelperBar } from './ShortcutPrompts.js';
import type { IconName } from './ui/icons.js';

/**
 * The shell's half of the shortcut / pie / search / undo plumbing: handlers
 * for the core commands, the global pies, the built-in search providers and
 * the undo sources of the document stores. Mounted once by the Workspace.
 */

/** The panel the user last clicked into (Ctrl+Space maximizes its group). */
let focusedPanel: string | null = null;
export function setFocusedPanel(panel: string): void {
  focusedPanel = panel;
}

function setView(view: string): void {
  useWorkspace.getState().setView(view as WorkspaceView);
}

/** Focus a view and bring its panel on screen (reopening it if it was closed). */
export function showView(view: string): void {
  setView(view);
  useLayout.getState().show(view);
}

function applyPreset(id: string): void {
  const view = useLayout.getState().applyPreset(id);
  if (view) setView(view);
}

const HANDLERS: Record<string, { run: () => void; enabled?: () => boolean }> = {
  ...Object.fromEntries(WORKSPACE_TABS.map((t) => [tabCommandId(t.id), { run: () => showView(t.id) }])),
  'view.settings': { run: () => openSettings('shortcuts') },
  'view.shortcuts': { run: () => openSettings('shortcuts') },
  'view.history': {
    run: () => {
      const layout = useLayout.getState();
      if (isVisible('history')) layout.toggle('history');
      else layout.show('history');
    },
  },
  'view.build': {
    run: () => {
      const layout = useLayout.getState();
      if (isVisible('build')) layout.toggle('build');
      else layout.show('build');
    },
  },
  'view.particles': { run: () => toggleParticleEditor(isVisible(PARTICLE_EDITOR_PANEL)) },
  'view.ai': { run: () => useWorkspace.getState().toggleAi() },
  'view.helper-bar': { run: () => useHelperBar.getState().toggle() },
  // Blender's Shift+R / Ctrl+Alt+R: run the last command again, or pick from the recent ones.
  'edit.repeat-last': {
    run: () => {
      const last = recentCommands()[0];
      if (last) shortcuts.run(last);
    },
    enabled: () => recentCommands().length > 0,
  },
  'edit.repeat-history': { run: () => openSearch('search', 'r:') },
  'view.explorer': {
    run: () => {
      useWorkspace.getState().setCodeSide('explorer');
      setView('code');
    },
  },
  'view.git': {
    run: () => {
      useWorkspace.getState().setCodeSide('git');
      setView('code');
    },
  },
  'view.search': { run: () => setView('code') },
  'palette.open': { run: () => toggleSearch('palette') },
  'search.open': { run: () => toggleSearch('search') },
  'favorites.open': { run: () => openFavoritesAtPointer() },
  'pie.tabs': { run: () => openPie('tabs') },
  'pie.window': { run: () => openPie('window') },
  'pie.workspaces': { run: () => openPie('workspaces') },
  'layout.maximize': { run: () => useLayout.getState().maximize(focusedPanel ?? useWorkspace.getState().view) },
  'layout.reset': { run: () => useLayout.getState().reset() },
  'layout.save': { run: () => void saveLayoutAs() },
  'edit.undo': {
    run: () => void undoService.undo(useWorkspace.getState().view),
    enabled: () => (undoService.active(useWorkspace.getState().view)?.snapshot().past.length ?? 0) > 0,
  },
  'edit.redo': {
    run: () => void undoService.redo(useWorkspace.getState().view),
    enabled: () => (undoService.active(useWorkspace.getState().view)?.snapshot().future.length ?? 0) > 0,
  },
  'file.save-all': {
    run: () => {
      void useScene.getState().flush();
      void useModel.getState().flush();
      void usePixel.getState().flush();
      void useSound.getState().flush();
    },
  },
  'file.save': {
    run: () => {
      const ws = useWorkspace.getState();
      const path = ws.activePath();
      if (path) void ws.saveFile(path);
    },
    enabled: () => useWorkspace.getState().activePath() !== null,
  },
  'file.close-project': { run: () => useWorkspace.getState().closeProject() },
  'play.toggle': {
    run: () => {
      const play = usePlay.getState();
      if (play.state === 'playing') play.stop();
      else play.play();
    },
  },
  'play.stop': { run: () => usePlay.getState().stop(), enabled: () => usePlay.getState().state !== 'stopped' },
  'play.pause': { run: () => usePlay.getState().togglePause() },
  'engine.reload': { run: () => retryPrism() },
};

function isVisible(panel: string): boolean {
  const el = document.querySelector(`[data-dock-panel="${panel}"]`) as HTMLElement | null;
  return Boolean(el?.isConnected && el.offsetParent !== null);
}

const ASSET_ICONS: Partial<Record<AssetKind, IconName>> = {
  folder: 'folder',
  scene: 'scene',
  model: 'model',
  pixel: 'pixel',
  sound: 'audio',
  video: 'camera',
  prefab: 'prefab',
  material: 'material',
  script: 'script',
  data: 'data',
  ui: 'ui',
  mesh: 'cube',
  texture: 'image',
  audio: 'audio',
  project: 'settings',
};

const ENTITY_ICONS: Record<ReturnType<typeof entityKind>, IconName> = {
  light: 'light',
  camera: 'camera',
  mesh: 'cube',
  terrain: 'mountain',
  audio: 'audio',
  particles: 'particles',
  liquid: 'water',
  empty: 'empty',
};

function selectEntity(id: string): void {
  setView('scene');
  useScene.getState().select([id]);
}

/** Right-click actions every command result offers (Blender: "Change Shortcut", "Add to Quick Favorites"). */
function commandActions(id: string) {
  return [{ id: 'shortcut', label: 'Change Shortcut…', icon: 'command', run: () => openShortcutsFor(id) }];
}

function recentItems(query: string, filtered: boolean): SearchItem[] {
  const out: SearchItem[] = [];
  recentCommands().forEach((id, i) => {
    const c = shortcuts.get(id);
    if (!c || c.external || (c.enabled && !c.enabled())) return;
    const match = filtered ? scoreOf(query, c.label, c.category ?? '') : 0;
    if (match === null) return;
    out.push({
      id: `recent:${id}`,
      label: c.label,
      detail: c.category,
      icon: c.icon,
      hint: keyHint(id),
      commandId: id,
      score: 100 - i + match,
      run: () => void shortcuts.run(id),
      actions: commandActions(id),
    });
  });
  return out;
}

function registerSearch(): Array<() => void> {
  return [
    registerSearchProvider({
      id: 'commands',
      label: 'Commands',
      prefix: '>',
      icon: 'command',
      showEmpty: true,
      priority: 100,
      search: (query) => {
        const out: SearchItem[] = [];
        const recent = recentCommands();
        for (const c of shortcuts.list()) {
          if (c.external || c.hidden || !c.run) continue;
          if (c.enabled && !c.enabled()) continue;
          const match = scoreOf(query, c.label, `${c.category ?? ''} ${c.keywords ?? ''} ${c.id}`);
          if (match === null) continue;
          // Recently used commands float up, as in Blender's F3.
          const rank = recent.indexOf(c.id);
          const score = match + (rank >= 0 ? 12 - Math.min(10, rank) : 0);
          out.push({
            id: `cmd:${c.id}`,
            label: c.label,
            detail: c.category,
            icon: c.icon,
            hint: keyHint(c.id),
            commandId: c.id,
            score,
            run: () => void shortcuts.run(c.id),
            actions: commandActions(c.id),
          });
        }
        return out;
      },
    }),
    registerSearchProvider({
      id: 'recent',
      label: 'Recent',
      prefix: 'r:',
      icon: 'refresh',
      showEmpty: true,
      priority: 110,
      emptyOnly: true,
      search: (query) => recentItems(query, Boolean(query)),
    }),
    registerSearchProvider({
      id: 'keys',
      label: 'Key bindings',
      prefix: 'k:',
      icon: 'command',
      explicit: true,
      priority: 40,
      search: (query) => {
        // "k: ctrl+j" lists everything bound to a chord; other text matches command names.
        const chord = parseChord(query);
        const out: SearchItem[] = [];
        for (const c of shortcuts.list()) {
          const keys = shortcuts.keysFor(c.id);
          if (keys.length === 0) continue;
          const score = chord ? (keys.includes(chord) ? 50 : null) : scoreOf(query, keys.map(formatChord).join(' '), c.label);
          if (score === null) continue;
          out.push({
            id: `key:${c.id}`,
            label: c.label,
            detail: `${c.category ?? ''}${c.external ? ' · bound by the view' : ''}`,
            icon: c.icon,
            hint: keys.map(formatChord).join('  '),
            score,
            run: () => openShortcutsFor(c.id),
          });
        }
        return out;
      },
    }),
    registerSearchProvider({
      id: 'files',
      label: 'Assets',
      prefix: 'a:',
      icon: 'folder',
      priority: 80,
      search: (query) => {
        const out: SearchItem[] = [];
        for (const e of useProjectFiles.getState().entries) {
          if (e.kind !== 'file') continue;
          const score = scoreOf(query, e.name, e.path);
          if (score === null) continue;
          out.push({
            id: `file:${e.path}`,
            label: e.name,
            detail: e.path,
            icon: ASSET_ICONS[assetKind(e)] ?? 'file',
            score,
            run: () => openAsset(e.path),
            actions: [
              { id: 'text', label: 'Open as Text', icon: 'code', run: () => openAsset(e.path, { asText: true }) },
              { id: 'copy', label: 'Copy Path', icon: 'copy', run: () => void navigator.clipboard?.writeText(e.path) },
            ],
          });
        }
        return out;
      },
    }),
    registerSearchProvider({
      id: 'entities',
      label: 'Entities',
      prefix: 'e:',
      icon: 'scene',
      priority: 70,
      search: (query) => {
        const doc = useScene.getState().doc;
        if (!doc) return [];
        const out: SearchItem[] = [];
        for (const e of doc.entities) {
          const name = entityName(e);
          const score = scoreOf(query, name, Object.keys(e.components ?? {}).join(' '));
          if (score === null) continue;
          const parent = e.parent ? doc.entities.find((p) => p.id === e.parent) : undefined;
          out.push({
            id: `entity:${e.id}`,
            label: name,
            detail: parent ? `in ${entityName(parent)}` : useScene.getState().path ?? undefined,
            icon: ENTITY_ICONS[entityKind(e)],
            score,
            run: () => selectEntity(e.id),
            actions: [{ id: 'copy', label: 'Copy Entity Id', icon: 'copy', run: () => void navigator.clipboard?.writeText(e.id) }],
          });
        }
        return out;
      },
    }),
    registerSearchProvider({
      id: 'components',
      label: 'Components',
      prefix: 'c:',
      icon: 'layers',
      priority: 60,
      search: (query) => {
        const doc = useScene.getState().doc;
        if (!doc || !query) return [];
        const out: SearchItem[] = [];
        for (const e of doc.entities) {
          for (const type of Object.keys(e.components ?? {})) {
            const score = scoreOf(query, type, entityName(e));
            if (score === null) continue;
            out.push({
              id: `component:${e.id}:${type}`,
              label: type,
              detail: `on ${entityName(e)}`,
              icon: 'layers',
              score,
              run: () => selectEntity(e.id),
            });
          }
        }
        return out;
      },
    }),
    registerSearchProvider({
      id: 'settings',
      label: 'Settings',
      prefix: 's:',
      icon: 'settings',
      priority: 50,
      search: (query) => {
        const items: Array<Omit<SearchItem, 'score'> & { extra?: string }> = [
          { id: 'set:providers', label: 'AI Providers', detail: 'Settings', icon: 'sparkles', extra: 'api keys claude codex models', run: () => openSettings('shortcuts') },
          { id: 'set:shortcuts', label: 'Keyboard Shortcuts', detail: 'Settings', icon: 'command', extra: 'keymap hotkeys bindings', run: () => openSettings('shortcuts') },
          { id: 'set:layout', label: 'Layouts & Workspaces', detail: 'Settings', icon: 'layers', extra: 'docking panels', run: () => openSettings('layout') },
          ...shortcuts.profiles().map((p) => ({
            id: `set:keymap:${p.id}`,
            label: `Keymap: ${p.name}`,
            detail: p.id === shortcuts.activeProfile ? 'Active keymap' : 'Switch keymap profile',
            icon: 'command',
            extra: 'profile shortcuts',
            run: () => shortcuts.setProfile(p.id),
          })),
          ...SETTINGS_SECTIONS.flatMap((section) =>
            section.fields.map((field) => ({
              id: `set:project:${section.key}.${field.key}`,
              label: `${section.label} › ${field.label}`,
              detail: `Project settings · settings.${section.key}.${field.key}`,
              icon: section.icon,
              extra: `project ${section.hint ?? ''}`,
              run: () => {
                // The Scene inspector shows project settings when nothing is selected.
                useScene.getState().select([]);
                setView('scene');
              },
            })),
          ),
        ];
        const out: SearchItem[] = [];
        for (const { extra, ...item } of items) {
          const score = scoreOf(query, item.label, `${item.detail ?? ''} ${extra ?? ''}`);
          if (score !== null) out.push({ ...item, score });
        }
        return out;
      },
    }),
  ];
}

function registerPies(): Array<() => void> {
  // Blender order: left, right, bottom, top, top-left, top-right, bottom-left, bottom-right.
  const tabOrder = ['scene', 'model', 'play', 'code', 'pixel', 'sound', 'board'];
  return [
    registerPie(
      {
        id: 'tabs',
        label: 'Workspace Tabs',
        items: () => {
          const view = useWorkspace.getState().view;
          return tabOrder.map((id) => {
            const tab = WORKSPACE_TABS.find((t) => t.id === id)!;
            const command = shortcuts.get(tabCommandId(tab.id));
            return { id, label: tab.label, icon: command?.icon, commandId: tabCommandId(tab.id), active: view === id };
          });
        },
      },
      false,
    ),
    registerPie(
      {
        id: 'window',
        label: 'Window & Layout',
        items: () => [
          { id: 'ai', label: 'AI Panel', icon: 'sparkles', commandId: 'view.ai', active: useLayout.getState().isOpen('ai') },
          { id: 'history', label: 'Undo History', icon: 'undo', commandId: 'view.history', active: useLayout.getState().isOpen('history') },
          { id: 'search', label: 'Search', icon: 'search', commandId: 'search.open' },
          { id: 'max', label: useLayout.getState().dock.maximized ? 'Restore Layout' : 'Maximize Panel', icon: 'fullscreen', commandId: 'layout.maximize' },
          { id: 'keys', label: 'Shortcuts', icon: 'command', commandId: 'view.shortcuts' },
          { id: 'save', label: 'Save Layout…', icon: 'layers', commandId: 'layout.save' },
          { id: 'reset', label: 'Reset Layout', icon: 'refresh', commandId: 'layout.reset' },
          { id: 'fav', label: 'Quick Favorites', icon: 'sparkles', commandId: 'favorites.open' },
        ],
      },
      false,
    ),
    registerPie(
      {
        id: 'workspaces',
        label: 'Workspaces',
        items: () =>
          WORKSPACE_PRESETS.slice(0, 8).map((p) => ({
            id: p.id,
            label: p.label,
            icon: 'layers',
            commandId: `layout.preset.${p.id}`,
            active: useLayout.getState().preset === p.id,
          })),
      },
      false,
    ),
  ];
}

/** Install the shell's commands, pies, search providers and undo sources. */
export function useShellCommands(): void {
  useEffect(() => {
    setViewContextProvider(() => useWorkspace.getState().view);
    const offs = [
      installShortcutDispatcher(window),
      shortcuts.register(CORE_COMMANDS.map((c) => ({ ...c, ...HANDLERS[c.id] }))),
      shortcuts.register(
        WORKSPACE_PRESETS.map((p) => ({
          id: `layout.preset.${p.id}`,
          label: `Workspace: ${p.label}`,
          category: 'Layout',
          icon: 'layers',
          keywords: `${p.description} preset layout`,
          run: () => applyPreset(p.id),
        })),
      ),
      shortcuts.register(LEGACY_COMMANDS),
      shortcuts.register(PROFILER_COMMANDS),
      shortcuts.register(PROCGEN_COMMANDS),
      ...registerPies(),
      ...registerSearch(),
      undoService.register(storeHistorySource({ id: 'scene', label: 'Scene', view: 'scene', store: useScene })),
      undoService.register(storeHistorySource({ id: 'model', label: 'Model', view: 'model', store: useModel })),
      undoService.register(storeHistorySource({ id: 'pixel', label: 'Pixel', view: 'pixel', store: usePixel })),
      undoService.register(storeHistorySource({ id: 'sound', label: 'Sound', view: 'sound', store: useSound })),
      undoService.register(storeHistorySource({ id: 'graph', label: 'Graph', view: 'graph', store: useGraph })),
    ];
    return () => offs.forEach((off) => off());
  }, []);
}
